(() => {
  const storage_key = "vendor_collection_handles_v2";
  const storage_ttl_ms = 10 * 60 * 1000; // 10 minutes
  const collection_products_limit = 250;
  const collection_products_max_pages = 40;
  const vendor_filter_param_fallback = "filter.p.vendor";
  const collection_vendor_entries_cache = new Map();

  const get_shopify_root = () => {
    const root = window?.Shopify?.routes?.root || "/";
    return root.endsWith("/") ? root : `${root}/`;
  };

  const safe_json_parse = (value) => {
    try {
      return JSON.parse(value);
    } catch (_err) {
      return null;
    }
  };

  const normalize_vendor_label = (value) => {
    if (typeof value !== "string") return "";
    return value.trim().toLowerCase().replace(/\s+/g, " ");
  };

  const remove_diacritics = (value) => {
    if (typeof value !== "string") return "";

    try {
      return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    } catch (_err) {
      return value;
    }
  };

  const to_vendor_handle = (vendor_label) => {
    const normalized_vendor_label = normalize_vendor_label(vendor_label);
    if (!normalized_vendor_label) return "";

    return remove_diacritics(normalized_vendor_label)
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const read_cached_collections = () => {
    try {
      const raw_value = window.sessionStorage.getItem(storage_key);
      if (!raw_value) return null;

      const parsed = safe_json_parse(raw_value);
      if (!parsed || typeof parsed !== "object") return null;

      const cached_at_ms = parsed.cached_at_ms;
      const collections = parsed.collections;

      if (typeof cached_at_ms !== "number") return null;
      if (!Array.isArray(collections)) return null;
      if (Date.now() - cached_at_ms > storage_ttl_ms) return null;

      const collection_records = [];
      collections.forEach((collection_item) => {
        if (!collection_item || typeof collection_item !== "object") return;

        const handle =
          typeof collection_item.handle === "string" ? collection_item.handle.trim() : "";
        if (!handle) return;

        const title = typeof collection_item.title === "string" ? collection_item.title : "";
        collection_records.push({ handle, title });
      });

      return collection_records;
    } catch (_err) {
      return null;
    }
  };

  const write_cached_collections = (collection_records) => {
    try {
      window.sessionStorage.setItem(
        storage_key,
        JSON.stringify({
          cached_at_ms: Date.now(),
          collections: collection_records,
        })
      );
    } catch (_err) {
      // Ignore cache write failures (storage disabled/quota exceeded).
    }
  };

  const fetch_collection_page = async ({ root, page, limit }) => {
    const url = `${root}collections.json?limit=${limit}&page=${page}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    });

    if (!response.ok) return null;
    return response.json();
  };

  const fetch_collection_products_page = async ({ root, collection_handle, page, limit }) => {
    const encoded_collection_handle = encodeURIComponent(collection_handle);
    const url = `${root}collections/${encoded_collection_handle}/products.json?limit=${limit}&page=${page}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    });

    if (!response.ok) return null;
    return response.json();
  };

  const fetch_all_collection_records = async () => {
    const cached_records = read_cached_collections();
    if (cached_records) return cached_records;

    const root = get_shopify_root();
    const limit = 250;
    const max_pages = 10;
    const collection_records = [];
    const seen_handles = new Set();

    for (let page = 1; page <= max_pages; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetch_collection_page({ root, page, limit });
      const collections = data?.collections;
      if (!Array.isArray(collections) || collections.length === 0) break;

      for (const c of collections) {
        const handle = c && typeof c.handle === "string" ? c.handle.trim() : "";
        if (!handle || seen_handles.has(handle)) continue;

        const title = c && typeof c.title === "string" ? c.title : "";
        collection_records.push({ handle, title });
        seen_handles.add(handle);
      }

      // If Shopify returns fewer than `limit`, there are no more pages.
      if (collections.length < limit) break;
    }

    write_cached_collections(collection_records);
    return collection_records;
  };

  const build_collection_indexes = (collection_records) => {
    const handle_set = new Set();
    const title_to_handle_map = new Map();

    collection_records.forEach(({ handle, title }) => {
      if (!handle) return;

      handle_set.add(handle);

      const normalized_title = normalize_vendor_label(title);
      if (!normalized_title || title_to_handle_map.has(normalized_title)) return;

      title_to_handle_map.set(normalized_title, handle);
    });

    return { handle_set, title_to_handle_map };
  };

  const split_filter_values = (raw_values) => {
    const values = [];

    raw_values.forEach((raw_value) => {
      if (typeof raw_value !== "string") return;

      raw_value.split(",").forEach((value_part) => {
        const value = value_part.trim();
        if (value.length) values.push(value);
      });
    });

    return values;
  };

  const get_active_vendor_set = ({ current_url, vendor_filter_param }) => {
    const filter_values = split_filter_values(current_url.searchParams.getAll(vendor_filter_param));
    const active_vendor_set = new Set();

    filter_values.forEach((filter_value) => {
      const normalized_filter_value = normalize_vendor_label(filter_value);
      if (normalized_filter_value) active_vendor_set.add(normalized_filter_value);
    });

    return active_vendor_set;
  };

  const build_vendor_filter_url = ({
    current_url,
    vendor_filter_param,
    vendor_label,
    is_active,
  }) => {
    const url = new URL(current_url.toString());
    const existing_values = split_filter_values(url.searchParams.getAll(vendor_filter_param));
    const normalized_vendor_label = normalize_vendor_label(vendor_label);
    const next_values = [];

    existing_values.forEach((value) => {
      if (normalize_vendor_label(value) !== normalized_vendor_label) {
        next_values.push(value);
      }
    });

    if (!is_active) {
      next_values.push(vendor_label);
    }

    url.searchParams.delete(vendor_filter_param);
    next_values.forEach((value) => {
      url.searchParams.append(vendor_filter_param, value);
    });
    url.searchParams.delete("page");

    return `${url.pathname}${url.search}${url.hash}`;
  };

  const sort_vendor_entries = (vendor_entries) => {
    return vendor_entries.sort((entry_a, entry_b) => {
      return entry_a.label.localeCompare(entry_b.label, undefined, { sensitivity: "base" });
    });
  };

  const fetch_collection_vendor_entries = async ({ collection_handle }) => {
    const collection_handle_value =
      typeof collection_handle === "string" ? collection_handle.trim() : "";
    if (!collection_handle_value) return [];

    if (collection_vendor_entries_cache.has(collection_handle_value)) {
      return collection_vendor_entries_cache.get(collection_handle_value);
    }

    const vendor_entries_promise = (async () => {
      const root = get_shopify_root();
      const vendor_map = new Map();

      for (let page = 1; page <= collection_products_max_pages; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const data = await fetch_collection_products_page({
          root,
          collection_handle: collection_handle_value,
          page,
          limit: collection_products_limit,
        });
        const products = data?.products;
        if (!Array.isArray(products) || products.length === 0) break;

        products.forEach((product) => {
          const vendor_label = typeof product?.vendor === "string" ? product.vendor.trim() : "";
          const normalized_vendor_label = normalize_vendor_label(vendor_label);
          if (!normalized_vendor_label) return;

          const existing_vendor_entry = vendor_map.get(normalized_vendor_label);
          if (existing_vendor_entry) {
            existing_vendor_entry.count += 1;
            return;
          }

          vendor_map.set(normalized_vendor_label, {
            label: vendor_label,
            count: 1,
          });
        });

        if (products.length < collection_products_limit) break;
      }

      return sort_vendor_entries(Array.from(vendor_map.values()));
    })();

    collection_vendor_entries_cache.set(collection_handle_value, vendor_entries_promise);

    try {
      return await vendor_entries_promise;
    } catch (_err) {
      collection_vendor_entries_cache.delete(collection_handle_value);
      throw _err;
    }
  };

  const build_vendor_link_item = ({
    vendor_entry,
    current_url,
    vendor_filter_param,
    active_vendor_set,
    is_last_item,
  }) => {
    const vendor_label = vendor_entry.label;
    const vendor_handle = to_vendor_handle(vendor_label);
    const normalized_vendor_label = normalize_vendor_label(vendor_label);
    const is_active = active_vendor_set.has(normalized_vendor_label);
    const filter_url = build_vendor_filter_url({
      current_url,
      vendor_filter_param,
      vendor_label,
      is_active,
    });

    const list_item = document.createElement("li");
    list_item.className = "m-link-lists--item";

    const link = document.createElement("a");
    link.className = `m-link${is_active ? " active" : ""}`;
    link.href = filter_url;
    link.textContent = vendor_label;
    link.dataset.vendorHandle = vendor_handle;
    link.dataset.vendorLabel = vendor_label;
    link.dataset.filterUrl = filter_url;
    link.dataset.vendorCount = String(vendor_entry.count);
    list_item.appendChild(link);

    if (!is_last_item) {
      const separator = document.createElement("span");
      separator.className = "m-custom-product-vendors__separator";
      separator.setAttribute("aria-hidden", "true");
      separator.textContent = " - ";
      list_item.appendChild(separator);
    }

    return list_item;
  };

  const update_empty_state = ({ list_el, has_vendors }) => {
    if (has_vendors) {
      list_el.removeAttribute("hidden");
    } else {
      list_el.setAttribute("hidden", "hidden");
    }

    const section_el = list_el.closest(".m-custom-product-vendors");
    if (!section_el) return;

    const empty_el = section_el.querySelector("[data-vendor-empty]");
    if (!empty_el) return;

    if (has_vendors) {
      empty_el.setAttribute("hidden", "hidden");
    } else {
      empty_el.removeAttribute("hidden");
    }
  };

  const hydrate_vendor_list = async (list_el) => {
    const collection_handle = list_el.dataset.collectionHandle;
    if (!collection_handle) return false;

    const vendor_filter_param = list_el.dataset.vendorFilterParam || vendor_filter_param_fallback;
    const vendor_entries = await fetch_collection_vendor_entries({ collection_handle });
    const has_vendors = Array.isArray(vendor_entries) && vendor_entries.length > 0;
    if (!has_vendors) {
      list_el.replaceChildren();
      update_empty_state({ list_el, has_vendors: false });
      return true;
    }

    const current_url = new URL(window.location.href);
    const active_vendor_set = get_active_vendor_set({ current_url, vendor_filter_param });
    const list_fragment = document.createDocumentFragment();

    vendor_entries.forEach((vendor_entry, index) => {
      const is_last_item = index === vendor_entries.length - 1;
      const list_item = build_vendor_link_item({
        vendor_entry,
        current_url,
        vendor_filter_param,
        active_vendor_set,
        is_last_item,
      });
      list_fragment.appendChild(list_item);
    });

    list_el.replaceChildren(list_fragment);
    update_empty_state({ list_el, has_vendors: true });
    return true;
  };

  const hydrate_vendor_lists = async (list_els) => {
    const hydrate_tasks = list_els.map(async (list_el) => {
      try {
        await hydrate_vendor_list(list_el);
      } catch (_err) {
        // If fetch/render fails, keep Liquid-rendered list untouched.
      }
    });

    await Promise.all(hydrate_tasks);
  };

  const collect_vendor_lists = () => {
    return Array.from(document.querySelectorAll("[data-vendor-list]"));
  };

  const collect_vendor_links = (list_els) => {
    const links = [];

    list_els.forEach((list_el) => {
      const list_links = list_el.querySelectorAll("a[data-vendor-handle]");
      list_links.forEach((link_el) => links.push(link_el));
    });

    return links;
  };

  const resolve_collection_handle = ({ vendor_handle, vendor_label, collection_indexes }) => {
    const { handle_set, title_to_handle_map } = collection_indexes;

    if (vendor_handle && handle_set.has(vendor_handle)) {
      return vendor_handle;
    }

    const normalized_vendor_label = normalize_vendor_label(vendor_label);
    if (!normalized_vendor_label) return null;

    return title_to_handle_map.get(normalized_vendor_label) || null;
  };

  const upgrade_vendor_links = (link_els, collection_indexes) => {
    const root = get_shopify_root();

    link_els.forEach((link_el) => {
      const vendor_handle = link_el.dataset.vendorHandle;
      const vendor_label = link_el.dataset.vendorLabel || link_el.textContent || "";
      const filter_url = link_el.dataset.filterUrl || link_el.getAttribute("href");

      if (!filter_url) return;

      const resolved_collection_handle = resolve_collection_handle({
        vendor_handle,
        vendor_label,
        collection_indexes,
      });

      if (resolved_collection_handle) {
        link_el.setAttribute("href", `${root}collections/${resolved_collection_handle}`);
        link_el.classList.remove("active");
        link_el.dataset.linkUpgraded = "true";
      } else {
        link_el.setAttribute("href", filter_url);
        link_el.dataset.linkUpgraded = "false";
      }
    });
  };

  const init_vendor_collection_links = async () => {
    if (window.__vendor_collection_links_initialized) return;
    window.__vendor_collection_links_initialized = true;

    const list_els = collect_vendor_lists();
    if (list_els.length === 0) return;

    await hydrate_vendor_lists(list_els);

    const link_els = collect_vendor_links(list_els);
    if (link_els.length === 0) return;

    try {
      const collection_records = await fetch_all_collection_records();
      const collection_indexes = build_collection_indexes(collection_records);
      upgrade_vendor_links(link_els, collection_indexes);
    } catch (_err) {
      // On failure, leave the Liquid-generated filter links as-is.
    }
  };

  window.addEventListener("DOMContentLoaded", () => {
    init_vendor_collection_links();
  });
})();

